import React, { useState } from "react";
import {
  Box,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Talent } from "../../../types/user";

import MediaSettings from "./TalentMediaSetting";
import SkillsAndTagsForm from "./TalentSkillsAndTags"; // ⇦ now uses "categories"
import DangerZone from "./DangerZone";

import { useAuth, useCurrentTalentProfile } from "../../../hooks";
import {
  useGetATalent,
  useUpdateTalent,
} from "../../../services/api/talent.api";
import { useGetCategories } from "../../../services/api/card.api";
import { CategoriesValue } from "../../../types/Card";
import { enqueueSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { GradientButton_ } from "../../../components/Buttons/GradientButton";
const lexend = { fontFamily: "'Lexend', sans-serif" };

// —— Netflix-ish tokens (visual only)
const COLORS = {
  bg: "#0b0b0b",
  panel: "#141414",
  text: "#ffffff",
  muted: "rgba(255,255,255,0.75)",
  border: "rgba(255,255,255,0.12)",
  red: "#E50914",
};
interface InputData {
  username: string;
  categories: string;
  bio: string;
  profile_image: string | File;
  cover_image: string | File;
  portfolio_links: string[];
  social_links: string[];
  skills?: string[];
}

/* ---------- File-upload helper ---------- */
const uploadFile = async (file: File) => {
  const fd = new FormData();
  fd.append("file", file);
  const r = await fetch("https://api.taasnet.com/api/v1/file-upload", {
    method: "POST",
    body: fd,
    headers: {
      Authorization: `Bearer ${localStorage.getItem("access_token") ?? ""}`,
    },
  });
  if (!r.ok) throw new Error("Upload failed");
  return (await r.json()).data.url as string;
};

/* ---------- Main component ---------- */
const TalentSettingsPage = ({ talentInfo }: { talentInfo: Talent }) => {
  const { userInfo } = useAuth();
  const { currentProfile } = useCurrentTalentProfile();
  const navigate = useNavigate();

  const { data: talent } = useGetATalent({
    key: ["talent", currentProfile?.id],
    params: { id: Number(currentProfile?.id) },
    options: { enabled: !!currentProfile?.id },
  });

  const { data: categories } = useGetCategories({ key: ["categories"] });
  const categoryList: CategoriesValue[] = categories?.data || [];

  const { update } = useUpdateTalent({
    key: ["talent-profile", talentInfo?.id],
    params: {
      id: parseInt(talentInfo?.id),
    },
  });

  /* ------ local prefs & availability ------ */
  const [availability, setAvailability] = useState({
    enabled: talent?.data?.coffe_call_enabled ?? false,
    price: talent?.data?.coffe_call_price ?? 0,
    duration: talent?.data?.coffe_call_duration ?? 30,
  });

  /* ------ MUI helpers ------ */
  const theme = useTheme();
  const downSm = useMediaQuery(theme.breakpoints.down("sm"));

  /* ------ Formik (InputData) ------ */
  const formik = useFormik<InputData>({
    enableReinitialize: true,
    initialValues: {
      username: talent?.data?.username ?? "",
      categories: talent?.data?.categories?.[0] ?? "",
      bio: talent?.data?.bio ?? "",
      profile_image: talent?.data?.profile_image ?? "",
      cover_image: talent?.data?.cover_image ?? "",
      portfolio_links: talent?.data?.portfolio_links ?? [],
      social_links: talent?.data?.social_links ?? [],
      skills: talent?.data?.skills ?? [],
    },
    validationSchema: Yup.object({
      username: Yup.string().min(2, "Too short").required("Required"),
      categories: Yup.string().required("Required"),
    }),
    onSubmit: async (vals) => {
      try {
        /* --- image upload if File objects --- */
        let profileUrl = vals.profile_image as string;
        let coverUrl = vals.cover_image as string;

        if (vals.profile_image instanceof File)
          profileUrl = await uploadFile(vals.profile_image);
        if (vals.cover_image instanceof File)
          coverUrl = await uploadFile(vals.cover_image);

        const payload = {
          ...vals,
          profile_image: profileUrl,
          cover_image: coverUrl,
          skills: vals.skills,
          categories: [vals.categories],
          coffe_call_enabled: availability.enabled,
          coffe_call_price: availability.price,
          coffe_call_duration: availability.duration,
          contact_preferences: "Both",
          coffe_call_availability: "Scheduled",
        };
        console.log("Submitting payload:", payload);
        await update(payload, {
          onSuccess: () => {
            enqueueSnackbar("Talent profile updated!", { variant: "success" });
            navigate("/talent/profile");
          },
        });
      } catch (err: any) {
        enqueueSnackbar(err?.message ?? "Update failed", { variant: "error" });
      }
    },
  });

  const canSave = formik.dirty && formik.isValid && !formik.isSubmitting;

  /* ---------- JSX ---------- */
  return (
    <Container maxWidth="lg" sx={{ mt: 4, ...lexend }}>
      <Paper
        sx={{
          p: { xs: 3, md: 5 },
          borderRadius: 3,
          backgroundColor: COLORS.panel,
          border: `1px solid ${COLORS.border}`,
          color: COLORS.text,
          boxShadow: "0 16px 48px rgba(0,0,0,0.5)",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          divider={
            <Divider
              flexItem
              orientation={downSm ? "horizontal" : "vertical"}
              sx={{ borderColor: COLORS.border }}
            />
          }
          spacing={4}
        >
          {/* LEFT column */}
          <Box component="form" flex={1} onSubmit={formik.handleSubmit}>
            <Typography variant="h4" fontWeight={600} mb={3} sx={{ color: COLORS.text }}>
              Talent Settings
            </Typography>

            {/* Media */}
            <MediaSettings
              profileUrl={
                typeof formik.values.profile_image === "string"
                  ? formik.values.profile_image
                  : undefined
              }
              coverUrl={
                typeof formik.values.cover_image === "string"
                  ? formik.values.cover_image
                  : undefined
              }
              onChange={(f) => {
                if (f.profile) formik.setFieldValue("profile_image", f.profile);
                if (f.cover) formik.setFieldValue("cover_image", f.cover);
              }}
            />
            {/* Basic text fields */}
            <Stack spacing={3} mt={4}>
              <TextField
                label="Full Name"
                name="username"
                value={formik.values.username}
                onChange={formik.handleChange}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "rgba(255,255,255,0.06)",
                    color: COLORS.text,
                    "& fieldset": {
                      borderColor: COLORS.border,
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(255,255,255,0.2)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: COLORS.red,
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: COLORS.muted,
                  },
                  "& .MuiInputBase-input": {
                    color: COLORS.text,
                  },
                }}
              />

              <TextField
                label="Bio"
                name="bio"
                multiline
                rows={3}
                value={formik.values.bio}
                onChange={formik.handleChange}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "rgba(255,255,255,0.06)",
                    color: COLORS.text,
                    "& fieldset": {
                      borderColor: COLORS.border,
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(255,255,255,0.2)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: COLORS.red,
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: COLORS.muted,
                  },
                  "& .MuiInputBase-input": {
                    color: COLORS.text,
                  },
                }}
              />
             
            </Stack>

            {/* Skills & Categories picker */}
            <Box mt={4}>
              <SkillsAndTagsForm
                skills={formik.values.skills ?? []}
                categories={formik.values.categories}
                categoryOptions={categoryList?.map(cat => ({ label: cat.name, value: cat.name }))}
                onChange={(key, values) => {
                  if (key === "skills") formik.setFieldValue("skills", values as string[]);
                  if (key === "categories")
                    formik.setFieldValue("categories", values as string);
                }}
              />
            </Box>

            {/* Save */}
            <Box mt={4} textAlign={{ xs: "center", md: "right" }}>
              <GradientButton_
                type="submit"
                variant="contained"
                disabled={!canSave}
                startIcon={
                  formik.isSubmitting ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : null
                }
                sx={{
                  background: COLORS.red,
                  fontFamily: "'Lexend', sans-serif",
                  textTransform: "none",
                  px: 4,
                  py: 1.3,
                  fontWeight: 500,
                  borderRadius: 2,
                  color: COLORS.text,
                  "&:hover": {
                    background: "#b20710",
                  },
                }}
              >
                {formik.isSubmitting ? "Saving…" : "Save Changes"}
              </GradientButton_>
            </Box>
          </Box>

          {/* RIGHT column */}
          <Box flex={1}>

            <DangerZone
              onDelete={() => console.log("DELETE")}
              onDeactivate={() => console.log("DEACTIVATE")}
            />
          </Box>
        </Stack>
      </Paper>
    </Container>
  );
};

export default TalentSettingsPage;
